import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-home-btn">
            <ArrowLeft size={16} className="mr-2" />
            Back to Home
          </Button>
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="The Interview Logo" className="w-8 h-8" />
            <h1 className="text-xl font-heading font-bold">The Interview</h1>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="bg-white p-8 md:p-12 border rounded-lg">
          <h1 className="text-4xl font-heading font-bold mb-4">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Last Updated: August 7, 2026</p>

          <div className="prose prose-slate max-w-none space-y-6">
            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">1. Introduction</h2>
              <p>
                Welcome to The Interview, a service provided by Nichols Global Business Services, LLC ("we," "our," or "us"). 
                We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy 
                Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
              </p>
              <p className="mt-2">
                By accessing or using The Interview platform, you agree to this Privacy Policy. If you do not agree with our 
                policies and practices, please do not use our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold mt-4 mb-2">2.1 Information You Provide to Us</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Account Information:</strong> Name, email address, password, phone number, company name (for employers)</li>
                <li><strong>Profile Information:</strong> Resume, work history, education, skills, professional photo</li>
                <li><strong>Payment Information:</strong> Processed securely through Stripe (we do not store full credit card numbers)</li>
                <li><strong>Communications:</strong> Messages sent through our platform, forum posts, chat messages</li>
                <li><strong>Video Interview Data:</strong> Scheduling information, in-room text chat, safety reports, and recordings or transcripts only when a separately disclosed feature is enabled</li>
              </ul>

              <h3 className="text-xl font-semibold mt-4 mb-2">2.2 Information Collected Automatically</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Usage Data:</strong> Pages viewed, features used, time spent on platform, click patterns</li>
                <li><strong>Device Information:</strong> IP address, browser type, operating system, device identifiers</li>
                <li><strong>Cookies and Tracking:</strong> Session data, preferences, analytics information</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">3. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide, maintain, and improve our services</li>
                <li>Process transactions and send related information</li>
                <li>Facilitate video interviews between candidates and companies</li>
                <li>Send administrative information, updates, and security alerts</li>
                <li>Respond to your comments, questions, and requests</li>
                <li>Monitor and analyze trends, usage, and activities</li>
                <li>Detect, prevent, and address technical issues and fraud</li>
                <li>Detect and review possible bullying, harassment, threats, discrimination, sexual exploitation, spam, and other safety violations</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">3.1 Automated and AI-Assisted Community Safety</h2>
              <p>
                Written community posts, chat messages, mentor profile content, mentor applications, and in-room interview text may be evaluated by automated safety systems. The systems can identify categories of potentially harmful content, block a submission, create a review record, and place a short temporary hold on an account after severe or repeated flags.
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>Safety review records may include a limited content excerpt, category, model score, account identifier, source location, and reviewer decision</li>
                <li>Human administrators make final account decisions and can restore accounts or dismiss incorrect reports</li>
                <li>Safety moderation is separate from candidate scoring and is not provided to employers as a hiring recommendation</li>
                <li>We do not analyze live camera or microphone feeds for this purpose, and we do not create a transcript without clear participant notice and consent</li>
                <li>When an external moderation provider is configured, written content necessary for the safety check may be sent securely to that service provider</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">4. How We Share Your Information</h2>
              
              <h3 className="text-xl font-semibold mt-4 mb-2">4.1 With Your Consent</h3>
              <p>
                We share your information when you direct us to do so, such as when you apply to a job posting or 
                book an interview with a company.
              </p>

              <h3 className="text-xl font-semibold mt-4 mb-2">4.2 Service Providers</h3>
              <p>We share information with third-party service providers who perform services on our behalf:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Payment Processing:</strong> Stripe for payment transactions</li>
                <li><strong>Email Services:</strong> Resend for transactional emails</li>
                <li><strong>Video Services:</strong> Video platform providers for interview functionality</li>
                <li><strong>Calendar Integration:</strong> Google Calendar for interview scheduling</li>
                <li><strong>Analytics:</strong> Service providers for usage analytics</li>
                <li><strong>Content Safety:</strong> Automated moderation providers when enabled, limited to information needed to evaluate submitted content</li>
              </ul>

              <h3 className="text-xl font-semibold mt-4 mb-2">4.3 Legal Requirements</h3>
              <p>We may disclose your information if required by law, subpoena, or legal process, or to protect our rights.</p>

              <h3 className="text-xl font-semibold mt-4 mb-2">4.4 Business Transfers</h3>
              <p>
                In the event of a merger, acquisition, or sale of assets, your information may be transferred to the 
                acquiring entity.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">5. Data Security</h2>
              <p>
                We implement appropriate technical and organizational security measures to protect your personal information, 
                including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Encryption of data in transit using SSL/TLS</li>
                <li>Secure password storage using bcrypt hashing</li>
                <li>Regular security audits and updates</li>
                <li>Access controls and authentication</li>
                <li>Secure payment processing through PCI-compliant providers</li>
              </ul>
              <p className="mt-2">
                However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security 
                of your information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">6. Your Rights and Choices</h2>
              
              <h3 className="text-xl font-semibold mt-4 mb-2">6.1 Access and Update</h3>
              <p>You can access and update your account information at any time through your profile settings.</p>

              <h3 className="text-xl font-semibold mt-4 mb-2">6.2 Delete Your Account</h3>
              <p>You may request deletion of your account by contacting us at privacy@theinterviewpros.com</p>

              <h3 className="text-xl font-semibold mt-4 mb-2">6.3 Marketing Communications</h3>
              <p>You can opt-out of promotional emails by clicking the unsubscribe link in any email.</p>

              <h3 className="text-xl font-semibold mt-4 mb-2">6.4 Cookies</h3>
              <p>You can control cookies through your browser settings. However, disabling cookies may affect functionality.</p>

              <h3 className="text-xl font-semibold mt-4 mb-2">6.5 Safety Decisions and Corrections</h3>
              <p>You may request human review of an automated safety action or correction of an inaccurate safety record by contacting safety@theinterviewpros.com.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">7. Data Retention</h2>
              <p>
                We retain your personal information for as long as necessary to provide our services and fulfill the purposes 
                outlined in this Privacy Policy. We may retain certain information as required by law or for legitimate business 
                purposes.
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>Active account data: Retained while account is active</li>
                <li>Deleted account data: Retained for 90 days before permanent deletion</li>
                <li>Transaction records: Retained for 7 years for tax and legal compliance</li>
                <li>Communications: Retained for 3 years for customer service purposes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">8. International Data Transfers</h2>
              <p>
                Your information may be transferred to and maintained on servers located outside of your state, province, 
                country, or other governmental jurisdiction. By using our services, you consent to the transfer of information 
                to the United States and other countries where our service providers operate.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">9. Children's Privacy</h2>
              <p>
                Our services are not intended for individuals under the age of 18. We do not knowingly collect personal 
                information from children. If we become aware that we have collected personal information from a child under 
                18, we will take steps to delete such information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">10. California Privacy Rights</h2>
              <p>
                If you are a California resident, you have certain rights under the California Consumer Privacy Act (CCPA):
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Right to know what personal information is collected</li>
                <li>Right to know if personal information is sold or disclosed</li>
                <li>Right to opt-out of the sale of personal information</li>
                <li>Right to delete personal information</li>
                <li>Right to non-discrimination for exercising your rights</li>
              </ul>
              <p className="mt-2">
                To exercise these rights, contact us at privacy@theinterviewpros.com
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">11. European Privacy Rights (GDPR)</h2>
              <p>
                If you are located in the European Economic Area (EEA), you have certain rights under the General Data 
                Protection Regulation (GDPR):
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Right to access your personal data</li>
                <li>Right to rectification of inaccurate data</li>
                <li>Right to erasure ("right to be forgotten")</li>
                <li>Right to restrict processing</li>
                <li>Right to data portability</li>
                <li>Right to object to processing</li>
                <li>Right to withdraw consent</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">12. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting 
                the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use of our services 
                after any changes indicates your acceptance of the updated Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-semibold mb-3">13. Contact Us</h2>
              <p>
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, 
                please contact us:
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded border">
                <p><strong>Nichols Global Business Services, LLC</strong></p>
                <p>The Interview Platform</p>
                <p className="mt-2">Email: privacy@theinterviewpros.com</p>
                <p>Email: support@theinterviewpros.com</p>
                <p>Website: www.theinterviewpros.com</p>
              </div>
            </section>

            <div className="mt-12 pt-8 border-t">
              <p className="text-sm text-muted-foreground">
                © 2025 Nichols Global Business Services, LLC. All rights reserved. The Interview is a trademark of 
                Nichols Global Business Services, LLC.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
